import Phaser from "phaser";
import {
  type TextDirection,
  resolveTextAlign,
  resolveOriginX,
  reorderForDirection,
} from "@yjsgames/i18n";

export interface DirectionalTextOptions {
  readonly dir: TextDirection;
  /** 논리 정렬 — `"start"`는 LTR=좌/RTL=우로 자동 변환. default `"start"`. */
  readonly align?: "start" | "center" | "end";
  readonly style: Phaser.Types.GameObjects.Text.TextStyle;
  /**
   * Bidi reorder 적용 여부 (default false). 단일 라인 라벨은 Canvas가 자체 Bidi 처리하므로 보통 불필요.
   * Phaser word-wrap 등으로 라인이 분할돼 run이 끊기는 경우에만 true (`reorderForDirection` 참조).
   */
  readonly applyBidi?: boolean;
}

/**
 * 방향 인지 Phaser 텍스트 — 논리 정렬(start/center/end)을 방향에 맞춰 물리 정렬/origin으로 자동 변환한다.
 * 모든 결정은 @yjsgames/i18n 순수 코어(`resolveTextAlign`/`resolveOriginX`/`reorderForDirection`)에 위임 —
 * 이 클래스 자체는 Phaser API 연결만 담당(로직 0%).
 *
 * 세로 origin은 기본 0.5(UI 라벨 세로 중앙). 호출자가 `setOrigin`으로 재조정 가능하며 `setDirection`은 그 y를 보존한다.
 */
export class DirectionalText extends Phaser.GameObjects.Text {
  private dir: TextDirection;
  private readonly logicalAlign: "start" | "center" | "end";
  private readonly useBidi: boolean;
  private logical: string;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    opts: DirectionalTextOptions,
  ) {
    const physical = resolveTextAlign(opts.align ?? "start", opts.dir);
    super(scene, x, y, "", { ...opts.style, align: physical });
    this.dir = opts.dir;
    this.logicalAlign = opts.align ?? "start";
    this.useBidi = opts.applyBidi ?? false;
    this.logical = text;
    this.setOrigin(resolveOriginX(physical), 0.5);
    super.setText(this.render());
  }

  /** 논리 텍스트 교체 — Bidi reorder(opt-in) 재적용. */
  setLogicalText(text: string): this {
    this.logical = text;
    super.setText(this.render());
    return this;
  }

  /** 방향 전환(언어 변경 등) — 정렬/origin/reorder 일괄 갱신. 세로 origin은 보존. */
  setDirection(dir: TextDirection): this {
    this.dir = dir;
    const physical = resolveTextAlign(this.logicalAlign, dir);
    this.setOrigin(resolveOriginX(physical), this.originY);
    this.setAlign(physical);
    super.setText(this.render());
    return this;
  }

  private render(): string {
    return this.useBidi ? reorderForDirection(this.logical, this.dir) : this.logical;
  }
}
